import { useState } from "react";
import { ArrowRight } from "lucide-react";
import DesignerPreview from "./DesignerPreview";
import { COURT_COLOURS } from "../data/content";

// Quick configurator (home page) — real per-sport line mechanics + sizing,
// powered by the same premium preview engine as the full Court Designer.
const SPORT_MAP = {
  Basketball: ["Basketball"],
  Tennis: ["Tennis"],
  Netball: ["Netball"],
  Futsal: ["Futsal"],
  Pickleball: ["Pickleball"],
  "Multi-Sport": ["Basketball", "Netball"],
};
const SIZES = [
  { id: "full", label: "Full · 28×15m" },
  { id: "half", label: "Half · 14×15m" },
  { id: "threes", label: "3x3 · 15×11m" },
  { id: "junior", label: "Junior · 10×7m" },
];

const Row = ({ label, value, onPick, testid }) => (
  <div>
    <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">
      {COURT_COLOURS.map((c) => (
        <button key={c.hex} title={c.name} data-testid={`${testid}-${c.name.replace(/\s/g, "-").toLowerCase()}`}
          onClick={() => onPick(c.hex)}
          className={`h-9 w-9 rounded-sm ring-2 transition-all ${value === c.hex ? "ring-neon scale-110" : "ring-white/10 hover:ring-white/40"}`}
          style={{ backgroundColor: c.hex }} />
      ))}
    </div>
  </div>
);

export default function CourtConfigurator({ onQuote }) {
  const [sport, setSport] = useState("Basketball");
  const [size, setSize] = useState("full");
  const [base, setBase] = useState("#5B6670");
  const [keyColor, setKeyColor] = useState("#141414");
  const [border, setBorder] = useState("#141414");
  const [line, setLine] = useState("#F2F2F2");

  return (
    <div className="grid lg:grid-cols-2 gap-10 items-start" data-testid="court-configurator">
      <div className="rounded-lg border border-white/10 bg-obsidian-800 p-6 md:p-10 grid-lines lg:sticky lg:top-28">
        <div className="flex items-center justify-between mb-6">
          <span className="font-mono text-[10px] uppercase tracking-widest text-neon">Live Preview</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{sport} · {SIZES.find((s) => s.id === size)?.label}</span>
        </div>
        <DesignerPreview zones={{ border, base, key: keyColor, centre: keyColor, lines: line }}
          sports={SPORT_MAP[sport]} size={size} logoText="SPEEDY RESURFACING" logoColor={line} />
      </div>

      <div className="space-y-7">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Sport / Layout</p>
          <div className="flex flex-wrap gap-2">
            {Object.keys(SPORT_MAP).map((s) => (
              <button key={s} onClick={() => setSport(s)} data-testid={`config-sport-${s.toLowerCase()}`}
                className={`rounded-sm border px-4 py-2.5 text-sm transition-colors ${sport === s ? "border-neon text-neon bg-neon/5" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Court Size</p>
          <div className="flex flex-wrap gap-2">
            {SIZES.map((s) => (
              <button key={s.id} onClick={() => setSize(s.id)} data-testid={`config-size-${s.id}`}
                className={`rounded-sm border px-4 py-2.5 text-sm transition-colors ${size === s.id ? "border-neon text-neon bg-neon/5" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <Row label="Base Surface" value={base} onPick={setBase} testid="config-base" />
        <Row label="Key Colour" value={keyColor} onPick={setKeyColor} testid="config-key" />
        <Row label="Border / Apron" value={border} onPick={setBorder} testid="config-border" />
        <Row label="Line Colour" value={line} onPick={setLine} testid="config-line" />

        <button data-testid="configurator-quote-btn"
          onClick={() => onQuote?.({ sport, size, base, key: keyColor, border, line })}
          className="inline-flex items-center gap-2 rounded-sm bg-neon px-7 py-4 font-bold text-obsidian shadow-neon hover:bg-neon-hover transition-colors">
          Get This Court Quoted <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
